import { c as _c } from "react/compiler-runtime";
import { match } from 'ts-pattern';
import { selectHorizontalSeparatorsVisible } from '../../store/createEagGridStore';
import { useGridSettingsSafe } from '../../store/selectors/useGridSettingsSafe';
import { useEagGridSelector } from '../../store/useEagGridSelector';
import type { EagColumn } from '../../types/EagColumn';
import type { EagColumnState } from '../../types/EagColumnState';
import type { RowData } from '../../types/RowData';
import { LineHeightToPx } from '../../utils/lineHeightToValue';
import { GridRow } from './components/GridRow/GridRow';
import { SkeletonRow } from './SkeletonRow';
export type GridBodyProps = {
  columns: EagColumn[];
  columnStates: EagColumnState[];
  rows: RowData[];
  previousRows: RowData[];
  skeletonRowCount: number;
  isLoading: boolean;
  selectedRowIds: Set<string>;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  stripedRows?: boolean;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  actionColumnWidth?: number;
  isMobile?: boolean;
  disableAllActions?: boolean;
  // Selection props — controls whether rows render a RowSelectCheckbox
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  onToggleSelection?: (rowId: string) => void;
};
export function GridBody(props) {
  const $ = _c(65);
  const hasPreviousRows = props.previousRows.length > 0;
  let t0;
  if ($[0] !== props.skeletonRowCount) {
    t0 = Array.from({
      length: props.skeletonRowCount
    }, _temp);
    $[0] = props.skeletonRowCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const skeletonIndices = t0;
  const gridSettings = useGridSettingsSafe();
  const skeletonHeightPx = LineHeightToPx[gridSettings.rowHeight];
  const horizontalSeparators = useEagGridSelector(selectHorizontalSeparatorsVisible);
  let t1;
  if ($[2] !== hasPreviousRows || $[3] !== horizontalSeparators || $[4] !== props.actionColumnWidth || $[5] !== props.columnStates || $[6] !== props.columns || $[7] !== props.disableAllActions || $[8] !== props.firstPinnedRightKey || $[9] !== props.isLoading || $[10] !== props.isMobile || $[11] !== props.lastPinnedLeftKey || $[12] !== props.onActionClick || $[13] !== props.onRowClick || $[14] !== props.onToggleSelection || $[15] !== props.pinnedLeftOffsets || $[16] !== props.pinnedRightOffsets || $[17] !== props.previousRows || $[18] !== props.rowSelectMode || $[19] !== props.rows || $[20] !== props.selectedRowIds || $[21] !== props.stripedRows || $[22] !== skeletonHeightPx || $[23] !== skeletonIndices) {
    let t2;
    if ($[25] !== horizontalSeparators || $[26] !== props.actionColumnWidth || $[27] !== props.columnStates || $[28] !== skeletonHeightPx || $[29] !== skeletonIndices) {
      t2 = () => <tbody>{skeletonIndices.map(index => <SkeletonRow key={index} heightPx={skeletonHeightPx} columnStates={props.columnStates} horizontalSeparators={horizontalSeparators} actionColumnWidth={props.actionColumnWidth} />)}</tbody>;
      $[25] = horizontalSeparators;
      $[26] = props.actionColumnWidth;
      $[27] = props.columnStates;
      $[28] = skeletonHeightPx;
      $[29] = skeletonIndices;
      $[30] = t2;
    } else {
      t2 = $[30];
    }
    let t3;
    if ($[31] !== props.actionColumnWidth || $[32] !== props.columnStates || $[33] !== props.columns || $[34] !== props.disableAllActions || $[35] !== props.firstPinnedRightKey || $[36] !== props.isMobile || $[37] !== props.lastPinnedLeftKey || $[38] !== props.onActionClick || $[39] !== props.onRowClick || $[40] !== props.onToggleSelection || $[41] !== props.pinnedLeftOffsets || $[42] !== props.pinnedRightOffsets || $[43] !== props.previousRows || $[44] !== props.rowSelectMode || $[45] !== props.selectedRowIds || $[46] !== props.stripedRows) {
      t3 = () => <tbody><tr role="status" className="sr-only" aria-label="Loading" />{props.previousRows.map((row, index_0) => <GridRow key={row.id} columns={props.columns} columnStates={props.columnStates} row={row} isSelected={props.selectedRowIds.has(row.id)} onRowClick={props.onRowClick} pinnedLeftOffsets={props.pinnedLeftOffsets} pinnedRightOffsets={props.pinnedRightOffsets} isStriped={props.stripedRows && index_0 % 2 === 1} rowIndex={index_0} lastPinnedLeftKey={props.lastPinnedLeftKey} firstPinnedRightKey={props.firstPinnedRightKey} onActionClick={props.onActionClick} actionColumnWidth={props.actionColumnWidth} isMobile={props.isMobile} disableAllActions={props.disableAllActions} rowSelectMode={props.rowSelectMode} onToggleSelection={props.onToggleSelection} />)}</tbody>;
      $[31] = props.actionColumnWidth;
      $[32] = props.columnStates;
      $[33] = props.columns;
      $[34] = props.disableAllActions;
      $[35] = props.firstPinnedRightKey;
      $[36] = props.isMobile;
      $[37] = props.lastPinnedLeftKey;
      $[38] = props.onActionClick;
      $[39] = props.onRowClick;
      $[40] = props.onToggleSelection;
      $[41] = props.pinnedLeftOffsets;
      $[42] = props.pinnedRightOffsets;
      $[43] = props.previousRows;
      $[44] = props.rowSelectMode;
      $[45] = props.selectedRowIds;
      $[46] = props.stripedRows;
      $[47] = t3;
    } else {
      t3 = $[47];
    }
    let t4;
    if ($[48] !== props.actionColumnWidth || $[49] !== props.columnStates || $[50] !== props.columns || $[51] !== props.disableAllActions || $[52] !== props.firstPinnedRightKey || $[53] !== props.isMobile || $[54] !== props.lastPinnedLeftKey || $[55] !== props.onActionClick || $[56] !== props.onRowClick || $[57] !== props.onToggleSelection || $[58] !== props.pinnedLeftOffsets || $[59] !== props.pinnedRightOffsets || $[60] !== props.rowSelectMode || $[61] !== props.rows || $[62] !== props.selectedRowIds || $[63] !== props.stripedRows) {
      t4 = () => <tbody>{props.rows.map((row_0, index_1) => <GridRow key={row_0.id} columns={props.columns} columnStates={props.columnStates} row={row_0} isSelected={props.selectedRowIds.has(row_0.id)} onRowClick={props.onRowClick} pinnedLeftOffsets={props.pinnedLeftOffsets} pinnedRightOffsets={props.pinnedRightOffsets} isStriped={props.stripedRows && index_1 % 2 === 1} rowIndex={index_1} lastPinnedLeftKey={props.lastPinnedLeftKey} firstPinnedRightKey={props.firstPinnedRightKey} onActionClick={props.onActionClick} actionColumnWidth={props.actionColumnWidth} isMobile={props.isMobile} disableAllActions={props.disableAllActions} rowSelectMode={props.rowSelectMode} onToggleSelection={props.onToggleSelection} />)}</tbody>;
      $[48] = props.actionColumnWidth;
      $[49] = props.columnStates;
      $[50] = props.columns;
      $[51] = props.disableAllActions;
      $[52] = props.firstPinnedRightKey;
      $[53] = props.isMobile;
      $[54] = props.lastPinnedLeftKey;
      $[55] = props.onActionClick;
      $[56] = props.onRowClick;
      $[57] = props.onToggleSelection;
      $[58] = props.pinnedLeftOffsets;
      $[59] = props.pinnedRightOffsets;
      $[60] = props.rowSelectMode;
      $[61] = props.rows;
      $[62] = props.selectedRowIds;
      $[63] = props.stripedRows;
      $[64] = t4;
    } else {
      t4 = $[64];
    }
    t1 = match({
      isLoading: props.isLoading,
      hasPreviousRows,
      isEmpty: props.rows.length === 0 && !props.isLoading
    }).with({
      isLoading: true,
      hasPreviousRows: false
    }, t2).with({
      isLoading: true,
      hasPreviousRows: true
    }, t3).with({
      isEmpty: true
    }, _temp2).otherwise(t4);
    $[2] = hasPreviousRows;
    $[3] = horizontalSeparators;
    $[4] = props.actionColumnWidth;
    $[5] = props.columnStates;
    $[6] = props.columns;
    $[7] = props.disableAllActions;
    $[8] = props.firstPinnedRightKey;
    $[9] = props.isLoading;
    $[10] = props.isMobile;
    $[11] = props.lastPinnedLeftKey;
    $[12] = props.onActionClick;
    $[13] = props.onRowClick;
    $[14] = props.onToggleSelection;
    $[15] = props.pinnedLeftOffsets;
    $[16] = props.pinnedRightOffsets;
    $[17] = props.previousRows;
    $[18] = props.rowSelectMode;
    $[19] = props.rows;
    $[20] = props.selectedRowIds;
    $[21] = props.stripedRows;
    $[22] = skeletonHeightPx;
    $[23] = skeletonIndices;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  return t1;
}
function _temp2() {
  return <tbody />;
}
function _temp(_, i) {
  return i;
}